import React, { useState } from "react";
import { apiPost } from "../../../services/api";
import { emitFinanceEvent } from "../finance/financeDispatcher";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";
import "./consultants.css";

interface LedgerEntry {
  id: number;
  type?: string;
  consultant_name?: string;
  procedure?: string;
  fee?: number;
  balance?: number;
}

interface ConsultantPaymentModalProps {
  entry: LedgerEntry;
  onClose: () => void;
  onPaid: () => void;
}

export default function ConsultantPaymentModal({ entry, onClose, onPaid }: ConsultantPaymentModalProps) {
  const { showToast } = useToast();
  const [mode, setMode] = useState("Cash");
  const [loading, setLoading] = useState(false);
  const amountDue = entry.balance ?? entry.fee ?? 0;
  const [amount, setAmount] = useState(String(amountDue));

  const handlePay = async () => {
    const payAmount = Number(amount || 0);
    if (payAmount <= 0) {
      showToast({ message: "Enter a valid payment amount.", type: "warning" });
      return;
    }
    if (payAmount > amountDue) {
      showToast({ message: "Payment amount cannot exceed the balance due.", type: "warning" });
      return;
    }

    setLoading(true);
    try {
      await apiPost("/api/manager/consultants/pay", {
        fee_id: entry.id,
        fee_type: entry.type || "CLINICAL",
        payment_mode: mode,
        amount: payAmount
      });
      showToast({ message: "Payment processed successfully.", type: "success" });
      
      // Emit Finance Event for Consultant Payment (Expenditure)
      if (payAmount > 0) {
        await emitFinanceEvent({
          source: "CONSULTANT_PAYMENT",
          ref_id: String(entry.id),
          debit: 0,
          credit: payAmount,
          description: `Consultant payout to ${entry.consultant_name || "Consultant"} for ${entry.procedure || "Service"}`,
          meta: {
            fee_id: entry.id,
            consultant_name: entry.consultant_name,
            method: mode
          }
        });
      }
      onPaid();
    } catch (e) {
      showToast({ message: "Payment failed. Please try again.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const remaining = Math.max(amountDue - (Number(amount) || 0), 0);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ width: '400px', background: '#fff', padding: '24px', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', color: '#0f172a' }}>Consultant Payout</h3>
        <p style={{ margin: '0 0 20px 0', fontSize: '0.9rem', color: '#64748b' }}>
          Paying <strong>{entry.consultant_name || "Consultant"}</strong> for {entry.procedure || "Service"}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', border: '1px solid #e2e8f0' }}>
            <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Amount Due:</span>
            <strong style={{ color: '#0f172a', fontSize: '1rem' }}>₹{amountDue}</strong>
          </div>

          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Pay Amount (₹)</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={0}
              max={amountDue}
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', boxSizing: 'border-box' }}
              autoFocus
            />
            <div style={{ marginTop: '6px', fontSize: '0.8rem', color: remaining > 0 ? '#d97706' : '#16a34a', fontWeight: 600 }}>
              Remaining Balance: ₹{remaining}
            </div>
          </div>

          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Payment Mode</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {["Cash", "Bank Transfer", "UPI"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  style={{ flex: 1, padding: '8px', borderRadius: '6px', border: mode === m ? '2px solid #0ea5e9' : '1px solid #cbd5e1', background: mode === m ? '#e0f2fe' : '#fff', color: mode === m ? '#0369a1' : '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.2s' }}
                >
                  {m === "Bank Transfer" ? "Bank" : m}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handlePay} isLoading={loading}>
            Pay ₹{Number(amount) || 0}
          </Button>
        </div>
      </div>
    </div>
  );
}
